import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSSE } from "@/hooks/useSSE";
import { clearOutcomeCache } from "@/lib/measure-outcomes";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useActiveProgram } from "@/platform/useActiveProgram";
import { useAco } from "@/contexts/AcoContext";
import { useUserPractices } from "@/hooks/queries/useMipsQueries";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Search, 
  Download, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Calendar,
  RefreshCw,
  RefreshCcw,
  Trash2,
  Eye
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiClient } from "@/lib/api-client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface UploadRecord {
  id: string;
  fileName: string;
  fileSize: number;
  uploadDate: string;
  status: "completed" | "failed" | "partial" | "deleted" | "phase1" | "phase2" | "phase3" | "processing";
  recordsProcessed: number;
  recordsFailed: number;
  issueSummary?: string | null;
  uploadedBy: string;
  processingTime: number; // in seconds
  errors?: string[];
  batchId?: string;
  /** R14: declared collection-type lane for APP Plus uploads (Medicare CQM |
   *  MIPS CQM | eCQM). Null for MIPS uploads and legacy batches. */
  collectionType?: string | null;
  practiceAlias?: string;
  practiceTin?: string;
  practiceId?: number;
}

/** What "Delete {year} Data" acts on: a MIPS practice or an ACO's shadow
 *  practice (APP Plus mode). `practiceId` is what the backend endpoint takes. */
interface DeleteTarget {
  practiceId: number;
  label: string;
}

export function UploadHistory() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("7days");
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [issuesUpload, setIssuesUpload] = useState<UploadRecord | null>(null);
  const [issueRows, setIssueRows] = useState<Array<{
    row: number | null;
    field: string;
    value: string;
    reason: string;
    type?: string;
  }>>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [deleteFileTarget, setDeleteFileTarget] = useState<UploadRecord | null>(null);
  const { programYear } = useProgramYear();
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const { toast } = useToast();
  const { selectedPractice } = useAuth();
  const queryClient = useQueryClient();
  const practicesQ = useUserPractices();
  const userPractices = practicesQ.data ?? [];

  // APP Plus mode: deletes target the selected ACO's shadow practice, not a
  // MIPS practice (shadow rows are excluded from /api/user/practices by
  // design, so without this branch ACO data was undeletable from the UI).
  const activeProgram = useActiveProgram();
  const { acos, selectedAco } = useAco();
  const appMode = activeProgram === 'app-plus' && acos.length > 0;

  const deleteTargets: DeleteTarget[] = appMode
    ? acos
        .filter(a => a.shadow_practice_id)
        .map(a => ({
          practiceId: a.shadow_practice_id as number,
          label: `ACO ${a.aco_id_cms} — ${a.name}`,
        }))
    : userPractices.map(p => ({
        practiceId: p.id,
        label: `${p.practice_alias || p.tin_name} (TIN: ${p.tin})`,
      }));

  // Upload history is scoped to the entity the user is currently working on
  // (top-bar practice, or the selected ACO's shadow practice in APP Plus mode).
  // Cross-practice browsing was removed 2026-08-11 by owner decision — everyone
  // sees only the current practice's uploads, regardless of who uploaded them.
  const scopedPracticeId = appMode
    ? (selectedAco?.shadow_practice_id ?? acos[0]?.shadow_practice_id) ?? null
    : selectedPractice?.id ?? null;

  // Fetch upload history
  const { data: uploads, isLoading, refetch } = useQuery<UploadRecord[]>({
    queryKey: ["upload-history", dateRange, statusFilter, scopedPracticeId],
    enabled: scopedPracticeId != null,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateRange !== "all") params.append("days", dateRange.replace("days", ""));
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("practiceId", String(scopedPracticeId));
      
      const response = await fetch(`${apiUrl}/api/claims/upload-history?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch upload history");
      const responseData = await response.json();
      return responseData.data || responseData;
    },
    // Polling fallback: SSE invalidation (below) is the primary refresh
    // mechanism, but the SSE connection drops whenever the API restarts or an
    // event fires before this page mounts — leaving spinners stuck or new
    // uploads invisible until navigation. Poll fast (5s) while any visible row
    // is in flight, and slow (20s) otherwise — the slow tick is what picks up
    // BRAND-NEW uploads that aren't in the cached data yet (a new upload can't
    // trigger the fast tick because its row isn't visible until a refetch).
    refetchInterval: (query) => {
      const rows = query.state.data;
      if (!Array.isArray(rows)) return 20000;
      const inFlight = rows.some(u =>
        u.status === "phase1" || u.status === "phase2" ||
        u.status === "phase3" || u.status === "processing");
      return inFlight ? 5000 : 20000;
    },
  });
  
  // Listen for SSE updates to refresh upload history
  useSSE(`${apiUrl}/api/sse/upload-progress`, {
    onMessage: useCallback((data) => {
      // Refresh the upload history when phase status changes
      if (data.phase && (data.status === 'processing' || data.status === 'completed' || data.status === 'failed')) {
        queryClient.invalidateQueries({ queryKey: ["upload-history"] });
      }
    }, [queryClient]),
    onError: useCallback((error) => {
      console.error("[SSE Error in UploadHistory]:", error);
    }, []),
  });

  const filteredUploads = uploads?.filter(upload =>
    upload.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    upload.uploadedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (upload.practiceAlias && upload.practiceAlias.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (upload.practiceTin && upload.practiceTin.includes(searchTerm))
  );

  // R14: show the Collection Type column only when at least one visible row is
  // a typed (APP Plus) upload — MIPS-only practices never see the extra column.
  const showCollectionType = !!filteredUploads?.some(u => u.collectionType);

  // Delete all practice data mutation
  const deleteAllDataMutation = useMutation({
    mutationFn: async (confirmTextValue: string) => {
      if (!deleteTarget) {
        throw new Error(appMode
          ? 'No ACO selected. Please select an ACO first.'
          : 'No practice selected. Please select a practice first.');
      }

      const response = await fetch(`${apiUrl}/api/claims/delete-practice-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          practiceId: deleteTarget.practiceId,
          program_year: programYear,
          confirmText: confirmTextValue
        }),
      });
      
      if (!response.ok) {
        // Try to parse as JSON, but fallback to text if it fails
        let errorMessage = 'Failed to delete practice data';
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch {
          // If JSON parsing fails, try to get text
          try {
            const textError = await response.text();
            errorMessage = textError || errorMessage;
          } catch {
            // Use default error message
          }
        }
        throw new Error(errorMessage);
      }
      
      return response.json();
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries();
      clearOutcomeCache();
      // Handle standardized response format - data is wrapped in response.data
      const details = response.data?.details || response.details;
      if (details) {
        toast({
          title: "Data Deleted",
          description: `All ${details.programYear ?? programYear} data for ${details.practiceName} has been permanently deleted. ${details.recordsDeleted.encounters} encounters, ${details.recordsDeleted.outcomes} outcomes removed.`,
        });
      } else {
        // Fallback if details are missing
        toast({
          title: "Data Deleted",
          description: `All ${programYear} data for ${deleteTarget?.label ?? 'the selected target'} has been permanently deleted.`,
        });
      }
      setShowDeleteAllModal(false);
      setDeleteAllConfirmText("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Per-file delete for failed/partial uploads: removes only that file's
  // encounters/outcomes (shared encounters are detached, not deleted).
  const deleteFileMutation = useMutation({
    mutationFn: async (upload: UploadRecord) => {
      return apiClient.delete(`/api/claims/${upload.id}`);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["upload-history"] });
      clearOutcomeCache();
      const data = response.data || response;
      toast({
        title: "File Deleted",
        description: data.message || "File and its data were removed.",
      });
      setDeleteFileTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message,
        variant: "destructive",
      });
      setDeleteFileTarget(null);
    },
  });

  const openIssues = async (upload: UploadRecord) => {
    setIssuesUpload(upload);
    setIssuesLoading(true);
    setIssueRows([]);
    try {
      const responseData = await apiClient.get(`/api/claims/upload-issues/${upload.id}`);
      const data = responseData.data || responseData;
      setIssueRows(data.issues || []);
    } catch (error) {
      toast({
        title: "Could not load issues",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
      setIssuesUpload(null);
    } finally {
      setIssuesLoading(false);
    }
  };

  const downloadIssues = async (uploadId: string, fileName: string) => {
    try {
      const response = await apiClient.request(`/api/claims/upload-issues/${uploadId}/export`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `issues_${fileName.replace(/\.[^/.]+$/, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Could not download the issues file.",
        variant: "destructive",
      });
    }
  };

  const downloadReport = async (uploadId: string, fileName: string) => {
    try {
      const response = await fetch(`${apiUrl}/api/claims/upload-report/${uploadId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      
      if (!response.ok) throw new Error("Failed to generate report");
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${fileName.replace(/\.[^/.]+$/, "")}_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Report Downloaded",
        description: `Processing report for ${fileName} has been downloaded.`,
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to generate the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "partial":
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case "deleted":
        return <Trash2 className="h-4 w-4 text-gray-500" />;
      case "phase1":
      case "phase2":
      case "phase3":
      case "processing":
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      default:
        return null;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (seconds: number) => {
    if (seconds == null) return "—";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Loading upload history...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="space-y-1.5">
              <CardTitle>Upload History</CardTitle>
              <CardDescription>
                View and download reports for previously uploaded files
              </CardDescription>
            </div>
            <Button 
              onClick={() => {
                // Pre-select the natural target: the working ACO in APP Plus
                // mode, otherwise the current/only practice.
                if (appMode) {
                  const aco = selectedAco ?? acos[0];
                  setDeleteTarget(deleteTargets.find(t => t.practiceId === aco?.shadow_practice_id) ?? deleteTargets[0] ?? null);
                } else if (deleteTargets.length === 1) {
                  setDeleteTarget(deleteTargets[0]);
                } else {
                  setDeleteTarget(deleteTargets.find(t => t.practiceId === selectedPractice?.id) ?? null);
                }
                setShowDeleteAllModal(true);
              }} 
              variant="destructive"
              size="sm"
              disabled={!uploads || uploads.length === 0 || deleteAllDataMutation.isPending}
              title={!uploads || uploads.length === 0 ? "No data to delete" : deleteAllDataMutation.isPending ? "Deleting data..." : "Delete all practice data"}
              className={cn(
                "transition-all duration-200",
                deleteAllDataMutation.isPending && "opacity-50 cursor-not-allowed"
              )}
            >
              {deleteAllDataMutation.isPending ? (
                <>
                  <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting {programYear} Data...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {programYear} Data
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by file name, user, or practice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Upload History Table */}
          {filteredUploads && filteredUploads.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    {showCollectionType && <TableHead>Collection Type</TableHead>}
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Practice</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUploads.map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div>
                            <span className="font-medium">{upload.fileName}</span>
                            {upload.issueSummary && (
                              <p className={cn(
                                "text-xs mt-0.5",
                                upload.issueSummary.startsWith("NOT CALCULATED") ? "text-red-600 font-medium" : "text-amber-600"
                              )}>
                                {upload.issueSummary}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      {showCollectionType && (
                        <TableCell>
                          {upload.collectionType ? (
                            <Badge variant="outline" className="border-primary/40 text-primary whitespace-nowrap">
                              {upload.collectionType}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {format(new Date(upload.uploadDate), "MMM d, yyyy h:mm a")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(upload.status)}
                          <Badge variant={
                            upload.status === "completed" ? "default" :
                            upload.status === "failed" ? "destructive" :
                            upload.status === "deleted" ? "outline" :
                            upload.status === "phase1" || upload.status === "phase2" || 
                            upload.status === "phase3" || upload.status === "processing" ? "secondary" :
                            "secondary"
                          }>
                            {upload.status === "phase1" ? "Phase 1" :
                             upload.status === "phase2" ? "Phase 2" :
                             upload.status === "phase3" ? "Phase 3" :
                             upload.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">
                            {upload.recordsProcessed.toLocaleString()} processed
                          </div>
                          {upload.recordsFailed > 0 && (
                            <div className="text-red-600">
                              {upload.recordsFailed.toLocaleString()} failed
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatFileSize(upload.fileSize)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{formatDuration(upload.processingTime)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{upload.uploadedBy}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{upload.practiceAlias || '-'}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(upload.issueSummary || upload.recordsFailed > 0) && upload.status !== "deleted" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openIssues(upload)}
                              title="View issues"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => downloadReport(upload.id, upload.fileName)}
                            disabled={upload.status === "deleted" || !upload.recordsProcessed}
                            title={upload.status === "deleted" ? "Cannot download report for deleted file" : "Download accepted-rows report"}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {(upload.status === "failed" || upload.status === "partial") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteFileTarget(upload)}
                              disabled={deleteFileMutation.isPending}
                              title="Delete this file and its data"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Uploads Found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== "all"
                  ? "No uploads match your search criteria."
                  : "No files have been uploaded yet."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Card */}
      {uploads && uploads.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <div className="text-2xl font-bold">{uploads.length}</div>
                <p className="text-sm text-muted-foreground">Total Uploads</p>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {uploads.filter(u => u.status === "completed").length}
                </div>
                <p className="text-sm text-muted-foreground">Successful</p>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {uploads.reduce((sum, u) => sum + u.recordsProcessed, 0).toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">Total Records</p>
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {formatFileSize(uploads.reduce((sum, u) => sum + Number(u.fileSize), 0))}
                </div>
                <p className="text-sm text-muted-foreground">Total Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!issuesUpload} onOpenChange={(open) => { if (!open) setIssuesUpload(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Issues — {issuesUpload?.fileName}</DialogTitle>
          </DialogHeader>
          {issuesUpload?.issueSummary?.startsWith("NOT CALCULATED") && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This file was <span className="font-semibold">not calculated</span>. Fix the
                blocked rows in your source file, <span className="font-semibold">delete this
                upload</span>, and upload the corrected file.
              </AlertDescription>
            </Alert>
          )}
          {issuesLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : issueRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No row-level details were saved for this upload.</p>
          ) : (
            <div className="max-h-[50vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issueRows.map((issue, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="whitespace-nowrap">{issue.row ?? "—"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={issue.type === "Warning" ? "outline" : "destructive"}
                          className={cn("whitespace-nowrap", issue.type === "Warning" && "border-amber-500 text-amber-700")}
                        >
                          {issue.type === "Warning" ? "Warning" : "Blocked"}
                        </Badge>
                      </TableCell>
                      <TableCell>{issue.field || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {issue.value ? `"${issue.value}"` : "(blank)"}
                      </TableCell>
                      <TableCell>{issue.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {issuesUpload && issuesUpload.recordsFailed > issueRows.filter(i => i.type !== "Warning").length && (
                <p className="text-xs text-muted-foreground mt-2">…and more</p>
              )}
            </div>
          )}
          <DialogFooter>
            {issuesUpload && (
              <Button
                variant="outline"
                onClick={() => downloadIssues(issuesUpload.id, issuesUpload.fileName)}
                disabled={issuesLoading || issueRows.length === 0}
              >
                <Download className="h-4 w-4 mr-1" />
                Download issues.xlsx
              </Button>
            )}
            {issuesUpload && (issuesUpload.status === "failed" || issuesUpload.status === "partial") && (
              <Button
                variant="destructive"
                onClick={() => {
                  const target = issuesUpload;
                  setIssuesUpload(null);
                  setDeleteFileTarget(target);
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete This File
              </Button>
            )}
            <Button onClick={() => setIssuesUpload(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-file delete confirmation */}
      <AlertDialog
        open={!!deleteFileTarget}
        onOpenChange={(open) => {
          if (!open && !deleteFileMutation.isPending) setDeleteFileTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteFileTarget?.fileName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the file and the data it created — its encounters,
              calculations, and outcomes. Visits that other files also contributed
              to are kept. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteFileMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteFileTarget && deleteFileMutation.mutate(deleteFileTarget)}
              disabled={deleteFileMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteFileMutation.isPending ? "Deleting…" : "Delete File"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Data Confirmation Modal */}
      <AlertDialog 
        open={showDeleteAllModal} 
        onOpenChange={(open) => {
          // Prevent closing the modal while deletion is in progress
          if (!deleteAllDataMutation.isPending) {
            setShowDeleteAllModal(open);
            if (!open) {
              setDeleteAllConfirmText(""); // Clear confirmation text when closing
            }
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all {programYear} data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every {programYear} upload for{' '}
              <span className="font-medium text-foreground">
                {deleteTarget?.label ?? (appMode ? 'the selected ACO' : 'the selected practice')}
              </span>
              {' '}— files, encounters, calculations, outcomes, manual edits, and
              processing history. Other performance years, measure selections, and
              system settings are not affected. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            {deleteTargets.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="delete-target-select">
                  {appMode ? 'ACO' : 'Practice'}
                </Label>
                <Select
                  value={deleteTarget?.practiceId?.toString() || ""}
                  onValueChange={(value) => {
                    setDeleteTarget(deleteTargets.find(t => t.practiceId.toString() === value) ?? null);
                  }}
                >
                  <SelectTrigger id="delete-target-select">
                    <SelectValue placeholder={appMode ? 'Select an ACO' : 'Select a practice'} />
                  </SelectTrigger>
                  <SelectContent>
                    {deleteTargets.map(target => (
                      <SelectItem key={target.practiceId} value={target.practiceId.toString()}>
                        {target.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="confirm-text">
                Type <span className="font-semibold">CONFIRM</span> to proceed
              </Label>
              <Input
                id="confirm-text"
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder="CONFIRM"
                autoComplete="off"
                disabled={deleteAllDataMutation.isPending}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setDeleteAllConfirmText("")}
              disabled={deleteAllDataMutation.isPending}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAllDataMutation.mutate(deleteAllConfirmText)}
              disabled={!deleteTarget || deleteAllConfirmText !== "CONFIRM" || deleteAllDataMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {programYear} Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
